import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { MentionsLegales, PolitiqueConfidentialite } from "./pages/Legal";
import ServiceDetail from "./pages/ServiceDetail";
import ChantierDetail from "./pages/ChantierDetail";
import ZonePage from "./pages/ZonePage";
import { ClientsBand } from "./components/ClientsBand";
import { useReveal } from "./hooks/useReveal";
import { useSeo } from "./hooks/useSeo";
import { CHANTIERS, SERVICE_SLUG_BY_ID, ZONES } from "./data/siteData";
import { Zap, Route as RouteIcon, Settings, Cable, Phone, Mail, MapPin, Menu, X, ChevronRight, CheckCircle, Clock, Users, Building2, ArrowRight, Send, MessageCircle } from "lucide-react";

// Social Icons
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useReveal();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="main-navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#141210] shadow-lg' : 'bg-[#0A0A0A]/90 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img 
              src="/images/logo-gold.png" 
              alt="A.C. ELEC Logo" 
              className="h-16 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-10">
            <Link to="/" data-testid="nav-accueil" className={`hover:text-[#D4A537] transition-colors font-medium ${isScrolled ? 'text-[#F5EFE3]' : 'text-white'}`}>Accueil</Link>
            <Link to="/services" data-testid="nav-services" className={`hover:text-[#D4A537] transition-colors font-medium ${isScrolled ? 'text-[#F5EFE3]' : 'text-white'}`}>Services</Link>
            <Link to="/realisations" data-testid="nav-realisations" className={`hover:text-[#D4A537] transition-colors font-medium ${isScrolled ? 'text-[#F5EFE3]' : 'text-white'}`}>Réalisations</Link>
            <Link to="/contact" data-testid="nav-contact" className={`hover:text-[#D4A537] transition-colors font-medium ${isScrolled ? 'text-[#F5EFE3]' : 'text-white'}`}>Contact</Link>
          </div>

          {/* CTA Button */}
          <Link to="/contact" data-testid="nav-devis-btn" className="hidden md:flex btn-primary items-center gap-2">
            Demander un Devis
            <ArrowRight className="w-4 h-4" />
          </Link>

          {/* Mobile Menu Button */}
          <button 
            data-testid="mobile-menu-btn"
            className={`md:hidden p-2 ${isScrolled ? 'text-[#F5EFE3]' : 'text-white'}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden bg-[#141210] border-t border-[#2E2820] py-6 animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="text-[#F5EFE3] hover:text-[#D4A537] font-medium py-2">Accueil</Link>
              <Link to="/services" onClick={() => setIsMenuOpen(false)} className="text-[#F5EFE3] hover:text-[#D4A537] font-medium py-2">Services</Link>
              <Link to="/realisations" onClick={() => setIsMenuOpen(false)} className="text-[#F5EFE3] hover:text-[#D4A537] font-medium py-2">Réalisations</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="text-[#F5EFE3] hover:text-[#D4A537] font-medium py-2">Contact</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="btn-primary text-center mt-4">Demander un Devis</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="main-footer" className="bg-[#0A0A0A] text-white footer-pattern">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo-gold.png" 
                alt="A.C. ELEC 94 Logo" 
                className="h-24 w-auto"
              />
            </div>
            <p className="text-[#8C8578] mb-6 max-w-md">
              Spécialisés dans les lots électriques pour le BTP en Île-de-France, nous accompagnons les acteurs majeurs du secteur avec fiabilité et professionnalisme.
            </p>
            <p className="text-[#A8A091] italic mb-6">"Notre savoir-faire, votre satisfaction"</p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href="https://wa.me/33664189291" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="social-whatsapp"
                className="w-10 h-10 border border-[#D4A537]/60 flex items-center justify-center text-[#D4A537] hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors rounded"
                title="Nous contacter sur WhatsApp"
              >
                <WhatsAppIcon />
              </a>
              <a 
                href="https://www.linkedin.com/in/claudia-madalina-patrascu-9028a6190" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="w-10 h-10 border border-[#D4A537]/60 flex items-center justify-center text-[#D4A537] hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors rounded"
                title="Suivez-nous sur LinkedIn"
              >
                <LinkedInIcon />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61576381062367" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 border border-[#D4A537]/60 flex items-center justify-center text-[#D4A537] hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors rounded"
                title="Suivez-nous sur Facebook"
              >
                <FacebookIcon />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Oswald'] font-bold text-lg mb-6">Liens Rapides</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-[#8C8578] hover:text-white transition-colors">Accueil</Link></li>
              <li><Link to="/services" className="text-[#8C8578] hover:text-white transition-colors">Nos Services</Link></li>
              <li><Link to="/realisations" className="text-[#8C8578] hover:text-white transition-colors">Réalisations</Link></li>
              <li><Link to="/contact" className="text-[#8C8578] hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Oswald'] font-bold text-lg mb-6">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-[#8C8578]">
                <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p>06 64 18 92 91</p>
                  <p>06 69 25 83 50</p>
                </div>
              </li>
              <li className="flex items-center gap-3 text-[#8C8578]">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href="mailto:contact@acelec94.com" className="hover:text-white transition-colors">contact@acelec94.com</a>
              </li>
              <li className="flex items-center gap-3 text-[#8C8578]">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>Île-de-France</span>
              </li>
            </ul>
          </div>
        </div>

        <div data-testid="footer-zones" className="border-t border-[#2E2820] mt-12 pt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <span className="text-[#F5EFE3] font-['Oswald'] mr-2">Électricien BTP :</span>
          {ZONES.map((z) => (
            <Link key={z.slug} to={`/electricien-btp/${z.slug}`} data-testid={`footer-zone-${z.slug}`} className="text-[#A8A091] hover:text-[#D4A537] transition-colors">{z.nom} ({z.code})</Link>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#2E2820] mt-12 pt-8 pb-20 md:pb-0 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#A8A091] text-sm">
            © {new Date().getFullYear()} A.C. ELEC 94. Tous droits réservés.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/mentions-legales" data-testid="footer-mentions-legales-link" className="text-[#A8A091] hover:text-[#D4A537] transition-colors">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" data-testid="footer-privacy-link" className="text-[#A8A091] hover:text-[#D4A537] transition-colors">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <a
      href="https://wa.me/33664189291?text=Bonjour,%20je%20souhaite%20obtenir%20un%20devis%20pour%20un%20projet%20%C3%A9lectrique."
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform animate-pulse"
      title="Contactez-nous sur WhatsApp"
    >
      <WhatsAppIcon />
    </a>
  );
};

// Home Page
const HomePage = () => {
  useSeo("Électricité BTP Île-de-France - TGBT, CFO, Câblage", "A.C. ELEC 94 - Expert en électricité BTP en Île-de-France. Raccordement TGBT, pose de chemins de câbles, tirage CFO, appareillages. Fiabilité et respect des délais sur chantier.", "/");
  const services = [
    {
      id: "tgbt",
      num: "01",
      titre: "Raccordement TGBT",
      description: "Installation et raccordement de Tableaux Généraux Basse Tension pour bâtiments tertiaires et industriels. Nos équipes maîtrisent parfaitement les normes NF C 15-100.",
      icon: Zap
    },
    {
      id: "chemins-cables",
      num: "02",
      titre: "Chemins de Câbles",
      description: "Pose de chemins de câbles métalliques et PVC adaptés aux contraintes architecturales de vos bâtiments. Solutions sur mesure pour toutes configurations.",
      icon: RouteIcon
    },
    {
      id: "cfo",
      num: "03",
      titre: "Tirage CFO",
      description: "Tirage de câbles Courants Forts pour l'alimentation électrique de vos installations. Expertise en distribution et raccordements haute section.",
      icon: Cable
    },
    {
      id: "appareillages",
      num: "04",
      titre: "Appareillages",
      description: "Raccordement de tous types d'appareillages électriques : éclairage LED professionnel, prises, interrupteurs et équipements techniques.",
      icon: Settings
    }
  ];

  const stats = [
    { value: "7", label: "Collaborateurs permanents" },
    { value: "30+", label: "Chantiers réalisés" },
    { value: "100%", label: "Délais respectés" },
    { value: "IDF", label: "Zone d'intervention" }
  ];

  const avantages = [
    {
      titre: "Expertise Technique Reconnue",
      description: "Notre équipe technique possède une expertise reconnue dans les lots électriques BTP. Nous intervenons sur des chantiers complexes pour des clients majeurs du secteur comme INEO, CEGELEC, LUXEO et EMI TERTIAIRE."
    },
    {
      titre: "Réactivité et Disponibilité",
      description: "Basés en Île-de-France, nous intervenons rapidement sur tous vos chantiers dans la région parisienne. Notre organisation nous permet de mobiliser nos équipes dans les meilleurs délais pour répondre à vos besoins urgents."
    },
    {
      titre: "Qualité et Conformité Garanties",
      description: "Chaque intervention est réalisée dans le strict respect des normes électriques en vigueur. Nous garantissons un travail soigné, des finitions impeccables et une documentation complète sur tous nos chantiers."
    }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/5667685/pexels-photo-5667685.jpeg)' }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 mb-8 animate-fade-in">
              <Zap className="w-4 h-4 text-[#F0CF6E]" />
              <span className="text-white/90 text-sm font-medium">Électricité BTP en Île-de-France</span>
            </div>
            
            <h1 className="font-['Oswald'] text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up">
              Expertise Électrique<br />
              <span className="gold-shimmer">BTP & Tertiaire</span>
            </h1>
            
            <p className="text-2xl text-[#F0CF6E] font-['Oswald'] italic mb-4 animate-fade-in-up animation-delay-100">
              "Notre savoir-faire, votre satisfaction"
            </p>
            
            <p className="text-xl text-white/80 mb-10 max-w-xl animate-fade-in-up animation-delay-200">
              Installation TGBT, Courants Forts et Raccordements. Fiabilité et respect des délais sur tous vos chantiers en Île-de-France.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/contact" data-testid="hero-devis-btn" className="btn-primary inline-flex items-center justify-center gap-2">
                Demander un Devis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" data-testid="hero-services-btn" className="btn-secondary border-white text-white hover:bg-[#D4A537] hover:border-[#D4A537] hover:text-[#0A0A0A] inline-flex items-center justify-center gap-2">
                Nos Services
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="bg-[#141210] py-16 border-b border-[#2E2820]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item pl-6 reveal" style={{ '--d': `${index * 100}ms` }}>
                <span className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#F5EFE3]">{stat.value}</span>
                <p className="text-[#A8A091] mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClientsBand />

      {/* Services Overview */}
      <section data-testid="services-overview" className="py-24 md:py-32 bg-[#0F0D0B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl mb-16 reveal">
            <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">Nos Services</span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#F5EFE3] mt-4 mb-6">
              Solutions Électriques Complètes
            </h2>
            <p className="text-[#A8A091] text-lg">
              Des équipes qualifiées et opérationnelles pour tous vos besoins en électricité BTP.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div 
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="service-card reveal bg-[#141210] border border-[#2E2820] p-8 group"
                style={{ '--d': `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="number-highlight text-3xl">{service.num}</span>
                  <service.icon className="w-8 h-8 text-[#D4A537] group-hover:scale-110 transition-transform" />
                </div>
                <h3 className="font-['Oswald'] text-2xl font-bold text-[#F5EFE3] mb-4">{service.titre}</h3>
                <p className="text-[#A8A091] mb-6">{service.description}</p>
                <Link 
                  to={`/services/${SERVICE_SLUG_BY_ID[service.id]}`}
                  data-testid={`home-service-link-${service.id}`}
                  className="inline-flex items-center gap-2 text-[#D4A537] font-semibold group-hover:gap-4 transition-all"
                >
                  En savoir plus
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-us-section" className="py-24 md:py-32 bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">Pourquoi Nous Choisir</span>
              <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#F5EFE3] mt-4 mb-8">
                Votre Partenaire Électricité de Confiance
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: CheckCircle, title: "Fiabilité Reconnue", desc: "Une réputation bâtie sur la qualité de nos interventions" },
                  { icon: Clock, title: "Respect des Délais", desc: "Engagement tenu sur chaque chantier, sans exception" },
                  { icon: Users, title: "Équipes Qualifiées", desc: "Techniciens expérimentés et formés aux dernières normes" },
                  { icon: Building2, title: "Grands Comptes BTP", desc: "Partenaire des acteurs majeurs du secteur" }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#D4A537]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#D4A537]" />
                    </div>
                    <div>
                      <h4 className="font-['Oswald'] text-lg font-bold text-[#F5EFE3]">{item.title}</h4>
                      <p className="text-[#A8A091]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative reveal">
              <img 
                src="https://images.pexels.com/photos/442160/pexels-photo-442160.jpeg" 
                alt="Ingénieur électricien professionnel"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#D4A537] text-[#0A0A0A] p-8">
                <span className="font-['Oswald'] text-5xl font-bold">15+</span>
                <p className="text-[#0A0A0A]/80">Années d'expertise</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="relative py-24">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1615774925655-a0e97fc85c14)' }}
        />
        <div className="cta-section absolute inset-0" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-6">
            Un Projet Électrique ?<br />Parlons-en !
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Nous sommes à votre disposition pour étudier vos besoins et vous accompagner dans la réalisation de vos projets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="cta-contact-btn" className="btn-primary inline-flex items-center justify-center gap-2">
              Demander un Devis Gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:0664189291" className="btn-secondary border-white text-white hover:bg-[#D4A537] hover:border-[#D4A537] hover:text-[#0A0A0A] inline-flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" />
              06 64 18 92 91
            </a>
          </div>
        </div>
      </section>

      {/* Expertise Section - Plus de contenu SEO */}
      <section className="py-24 bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">Notre Expertise</span>
              <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#F5EFE3] mt-4 mb-8">
                Électricien BTP Professionnel en Île-de-France
              </h2>
              <div className="prose text-[#A8A091] space-y-4">
                <p>
                  <strong>A.C. ELEC 94</strong> est une entreprise d'électricité spécialisée dans les lots électriques pour le secteur du BTP en Île-de-France. Fondée sur des valeurs de fiabilité et de professionnalisme, notre société accompagne les acteurs majeurs de la construction dans la réalisation de leurs projets électriques.
                </p>
                <p>
                  Notre équipe de <strong>7 collaborateurs permanents</strong> intervient sur tous types de chantiers : bâtiments tertiaires, centres commerciaux, data centers, résidences collectives, hôtels et complexes sportifs. Nous maîtrisons l'ensemble des interventions techniques liées à l'électricité du bâtiment.
                </p>
                <p>
                  Reconnus pour notre <strong>respect des délais</strong> et la qualité de nos prestations, nous travaillons en partenariat avec des entreprises renommées telles que <strong>INEO, LUXEO, CEGELEC, PHIBOR et EMI TERTIAIRE</strong>. Cette collaboration avec les leaders du secteur témoigne de notre sérieux et de notre expertise technique.
                </p>
              </div>
            </div>
            <div>
              <h3 className="font-['Oswald'] text-2xl font-bold text-[#F5EFE3] mb-6">Nos Domaines d'Intervention</h3>
              <div className="space-y-4">
                {avantages.map((avantage, index) => (
                  <div key={index} className="bg-[#0F0D0B] p-6 border-l-4 border-[#D4A537]">
                    <h4 className="font-['Oswald'] text-lg font-bold text-[#F5EFE3] mb-2">{avantage.titre}</h4>
                    <p className="text-[#A8A091] text-sm">{avantage.description}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-6 bg-[#0A0A0A] text-white">
                <h4 className="font-['Oswald'] text-lg font-bold mb-4">Zone d'Intervention</h4>
                <p className="text-white/80 text-sm mb-4">
                  Nous intervenons sur l'ensemble de l'Île-de-France : Paris, Hauts-de-Seine (92), Seine-Saint-Denis (93), Val-de-Marne (94), Val-d'Oise (95), Essonne (91), Yvelines (78) et Seine-et-Marne (77).
                </p>
                <p className="text-[#F0CF6E] font-semibold">
                  Devis gratuit sous 48h
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Services Page
const ServicesPage = () => {
  useSeo("Services électriques courants forts BTP", "Raccordement TGBT, pose de chemins de câbles, tirage CFO et raccordement des appareillages : les prestations courants forts d'A.C. ELEC 94 en Île-de-France.", "/services");
  const services = [
    {
      id: "tgbt",
      num: "01",
      titre: "Raccordement TGBT",
      description: "Installation et raccordement de Tableaux Généraux Basse Tension. Expertise complète en armoires électriques pour bâtiments tertiaires et industriels.",
      details: [
        "Conception et installation d'armoires électriques",
        "Raccordement et mise en service",
        "Conformité aux normes NF C 15-100",
        "Maintenance préventive et corrective"
      ],
      image: "/images/cfo-3.jpg",
      icon: Zap
    },
    {
      id: "chemins-cables",
      num: "02",
      titre: "Pose de Chemins de Câbles",
      description: "Installation de chemins de câbles métalliques et PVC. Solutions adaptées aux contraintes architecturales et aux normes en vigueur.",
      details: [
        "Chemins de câbles métalliques (fil, tôle)",
        "Goulottes PVC et aluminium",
        "Solutions anti-feu et étanches",
        "Adaptation aux contraintes du bâtiment"
      ],
      image: "/images/cfo-1.jpg",
      icon: RouteIcon
    },
    {
      id: "cfo",
      num: "03",
      titre: "Tirage CFO",
      description: "Tirage de câbles Courants Forts. Expertise en distribution électrique, alimentation et raccordements puissance.",
      details: [
        "Câblage courants forts (alimentation)",
        "Distribution électrique",
        "Raccordements puissance",
        "Câbles haute section"
      ],
      image: "/images/cfo-2.jpg",
      icon: Cable
    },
    {
      id: "appareillages",
      num: "04",
      titre: "Raccordement Appareillages",
      description: "Raccordement de tous types d'appareillages électriques : éclairage, prises, interrupteurs, équipements spécifiques.",
      details: [
        "Installation d'éclairage LED professionnel",
        "Pose de prises et interrupteurs",
        "Équipements techniques spécifiques",
        "Domotique et automatismes"
      ],
      image: "/images/cfo-4.jpg",
      icon: Settings
    }
  ];

  return (
    <main data-testid="services-page" className="pt-20">
      {/* Header */}
      <section className="bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">Nos Prestations</span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
            Services Électriques<br />BTP & Tertiaire
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Des solutions complètes pour tous vos besoins en électricité sur chantier. Équipes qualifiées et interventions maîtrisées.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div 
                key={service.id}
                data-testid={`service-detail-${service.id}`}
                className={`reveal grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="number-highlight text-5xl">{service.num}</span>
                    <service.icon className="w-10 h-10 text-[#D4A537]" />
                  </div>
                  <h2 className="font-['Oswald'] text-4xl font-bold text-[#F5EFE3] mb-6">{service.titre}</h2>
                  <p className="text-[#A8A091] text-lg mb-8">{service.description}</p>
                  
                  <ul className="space-y-4 mb-8">
                    {service.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#D4A537] flex-shrink-0" />
                        <span className="text-[#F5EFE3]">{detail}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-4">
                    <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                      Demander un Devis
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to={`/services/${SERVICE_SLUG_BY_ID[service.id]}`} data-testid={`services-detail-link-${service.id}`} className="btn-secondary inline-flex items-center gap-2">
                      En savoir plus
                    </Link>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img 
                    src={service.image}
                    alt={service.titre}
                    className="w-full h-[400px] object-cover shadow-xl"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#141210] border-y border-[#D4A537]/30 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-6">
            Besoin d'une intervention électrique ?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Contactez-nous pour discuter de votre projet et obtenir un devis personnalisé.
          </p>
          <Link to="/contact" className="bg-[#D4A537] text-[#0A0A0A] font-['Oswald'] font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center gap-2 hover:bg-[#F0CF6E] transition-colors">
            Nous Contacter
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

// Realisations Page
const RealisationsPage = () => {
  useSeo("Réalisations et chantiers", "Chantiers courants forts réalisés par A.C. ELEC 94 en Île-de-France : data centers, tours de bureaux, sites de recherche, pour INEO, PHIBOR, EMI TERTIAIRE.", "/realisations");
  const realisationsRecentes = [
    {
      id: 1,
      operation: "Tirage",
      image: "/images/tirage-1.jpg"
    },
    {
      id: 2,
      operation: "Tirage",
      image: "/images/tirage-2.jpg"
    },
    {
      id: 3,
      operation: "Distribution",
      image: "/images/distribution-1.jpg"
    },
    {
      id: 4,
      operation: "Raccordement TGBT",
      image: "/images/distribution-2.jpg"
    },
    {
      id: 5,
      operation: "Raccordement appareillage",
      image: "/images/tirage-3.jpg"
    },
    {
      id: 6,
      operation: "Raccordement TGBT",
      image: "/images/cfo-2.jpg"
    },
    {
      id: 7,
      operation: "Raccordement TD",
      image: "/images/cfo-3.jpg"
    },
    {
      id: 8,
      operation: "Chemin de câbles",
      image: "/images/cfo-1.jpg"
    },
    {
      id: 9,
      operation: "Raccordement TD",
      image: "/images/cfo-5.jpg"
    }
  ];

  const realisationsACELEC = CHANTIERS;

  const historiqueProjects = [
    { periode: "2025", chantier: "GARE DE LYON", client: "INEO", operation: "Raccordement TD" },
    { periode: "2025", chantier: "DATA4", client: "PHIBOR", operation: "Tirage" },
    { periode: "2025", chantier: "DATA4", client: "CEGELEC", operation: "Distribution" },
    { periode: "2025", chantier: "DIGITAL REALITY", client: "CEGELEC / PHIBOR", operation: "Tirage" },
    { periode: "2024", chantier: "TOUR TOTAL", client: "INEO", operation: "Raccordement TGBT" },
    { periode: "2024", chantier: "AIR LIQUIDE", client: "EMI TERTIAIRE", operation: "Raccordement TD" },
    { periode: "2024", chantier: "SEMARIS", client: "INEO", operation: "Distribution" },
    { periode: "2024", chantier: "POINT P NANTERRE", client: "LUXEO", operation: "Chemin de câbles" },
    { periode: "2024", chantier: "EPISAVEURS WISSOUS", client: "LUXEO", operation: "Distribution" },
    { periode: "2024", chantier: "CHADAPAUX", client: "LUXEO", operation: "Tirage" },
    { periode: "2024", chantier: "CRECHE", client: "LUXEO", operation: "Raccordement appareillage" },
    { periode: "2024", chantier: "ROUGIER EN PLE PARIS", client: "LUXEO", operation: "Raccordement TD" },
    { periode: "2024", chantier: "FERN", client: "FERN", operation: "Distribution" },
    { periode: "2023/2024", chantier: "BOUGIVAL", client: "INEO", operation: "Chemin de câbles" },
    { periode: "2023/2024", chantier: "APOLLONIS", client: "INEO", operation: "Distribution" },
    { periode: "2023", chantier: "DUO AMENAGEMENT PARIS 13", client: "INEO", operation: "Raccordement TGBT" },
    { periode: "2023", chantier: "KALIFORNIA", client: "PHIBOR", operation: "Tirage" },
    { periode: "2023", chantier: "FITNES PARK", client: "LUXEO", operation: "Raccordement appareillage" },
    { periode: "2023", chantier: "ENEDIS MAUREPAS", client: "INEO", operation: "Distribution" },
    { periode: "2023", chantier: "MASER ENGINEERING", client: "LUXEO", operation: "Raccordement TD" },
    { periode: "2023", chantier: "COLT DATA CENTER", client: "INEO", operation: "Tirage" },
    { periode: "2023", chantier: "ITALIE 2", client: "LUXEO", operation: "Chemin de câbles" },
    { periode: "2023", chantier: "CEDEO GRIGNY", client: "LUXEO", operation: "Raccordement TGBT" },
    { periode: "2022", chantier: "CEDEO PARIS 19", client: "LUXEO", operation: "Distribution" },
    { periode: "2022", chantier: "POINT P CHAVILLE", client: "LUXEO", operation: "Raccordement TD" }
  ];

  const clients = [
    { nom: "INEO" },
    { nom: "LUXEO" },
    { nom: "CEGELEC" },
    { nom: "PHIBOR" },
    { nom: "EMI TERTIAIRE" },
    { nom: "E CONCEPT" },
    { nom: "STCT" }
  ];

  return (
    <main data-testid="realisations-page" className="pt-20">
      {/* Header */}
      <section className="bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">Notre Portfolio</span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
            Nos Réalisations
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Découvrez nos chantiers réalisés en Île-de-France en collaboration avec les leaders du secteur de l'énergie et de la construction.
          </p>
        </div>
      </section>

      {/* Réalisations Récentes */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-3xl font-bold text-[#F5EFE3] mb-4">Réalisations A.C. ELEC 94</h2>
            <p className="text-[#A8A091]">Interventions caractérisées par une exigence de qualité et une maîtrise des délais.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {realisationsRecentes.map((project) => (
              <div 
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="group overflow-hidden reveal"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img 
                    src={project.image}
                    alt={project.operation}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <span className="text-white font-semibold">{project.operation}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Historique A.C. ELEC 94 */}
      <section className="py-16 bg-[#1C1914]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-3xl font-bold text-[#F5EFE3] mb-4">Chantiers A.C. ELEC 94</h2>
            <p className="text-[#A8A091]">Nos chantiers en Île-de-France</p>
          </div>
          
          <div className="bg-[#141210] rounded-sm shadow-lg overflow-hidden reveal">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-[#D4A537] text-[#0A0A0A]">
                  <tr>
                    <th className="px-6 py-4 text-left font-['Oswald']">Période</th>
                    <th className="px-6 py-4 text-left font-['Oswald']">Chantier</th>
                    <th className="px-6 py-4 text-left font-['Oswald']">Client</th>
                    <th className="px-6 py-4 text-left font-['Oswald']">Opération</th>
                  </tr>
                </thead>
                <tbody>
                  {realisationsACELEC.map((project, index) => (
                    <tr key={index} data-testid={`acelec-row-${index}`} className="border-b border-[#2E2820] hover:bg-[#0F0D0B]">
                      <td className="px-6 py-4 text-[#A8A091]">{project.periode}</td>
                      <td className="px-6 py-4 font-semibold text-[#F5EFE3]">
                        <Link to={`/realisations/${project.slug}`} data-testid={`acelec-link-${project.slug}`} className="hover:text-[#D4A537] inline-flex items-center gap-2 transition-colors group">
                          {project.chantier}
                          <ChevronRight className="w-4 h-4 text-[#D4A537] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-[#D4A537]">{project.client}</td>
                      <td className="px-6 py-4 text-[#A8A091]">{project.operation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Historique SICROM */}
      <section className="py-16 bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-3xl font-bold text-[#F5EFE3] mb-4">Héritage SICROM</h2>
            <p className="text-[#A8A091]">Expérience solide (2022-2025)</p>
          </div>
          
          <div className="bg-[#141210] rounded-sm shadow-lg overflow-hidden border border-[#2E2820] reveal">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-[#0A0A0A] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-['Oswald']">Période</th>
                    <th className="px-6 py-4 text-left font-['Oswald']">Chantier</th>
                    <th className="px-6 py-4 text-left font-['Oswald']">Client</th>
                    <th className="px-6 py-4 text-left font-['Oswald']">Opération</th>
                  </tr>
                </thead>
                <tbody>
                  {historiqueProjects.map((project, index) => (
                    <tr key={index} className="border-b border-[#2E2820] hover:bg-[#0F0D0B]">
                      <td className="px-6 py-4 text-[#A8A091]">{project.periode}</td>
                      <td className="px-6 py-4 font-semibold text-[#F5EFE3]">{project.chantier}</td>
                      <td className="px-6 py-4 text-[#D4A537]">{project.client}</td>
                      <td className="px-6 py-4 text-[#A8A091]">{project.operation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-24 bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-['Oswald'] text-4xl font-bold text-[#F5EFE3] mb-6">
            Ils nous font confiance
          </h2>
          <p className="text-[#A8A091] text-lg mb-12 max-w-2xl mx-auto">
            Nous collaborons avec les leaders du secteur de l'énergie et de la construction en Île-de-France.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {clients.map((client, index) => (
              <div 
                key={index} 
                className="bg-[#0F0D0B] p-6 flex items-center justify-center h-24 hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors group"
              >
                <span className="text-xl font-['Oswald'] font-bold text-[#A8A091] group-hover:text-white">{client.nom}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <span className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#D4A537]">7</span>
              <p className="text-white/70 mt-2">Collaborateurs permanents</p>
            </div>
            <div>
              <span className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#D4A537]">30+</span>
              <p className="text-white/70 mt-2">Chantiers réalisés</p>
            </div>
            <div>
              <span className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#D4A537]">100%</span>
              <p className="text-white/70 mt-2">Délais respectés</p>
            </div>
            <div>
              <span className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#D4A537]">IDF</span>
              <p className="text-white/70 mt-2">Zone d'intervention</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#141210] border-y border-[#D4A537]/30 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-6">
            Votre projet sera notre prochaine référence
          </h2>
          <Link to="/contact" className="bg-[#D4A537] text-[#0A0A0A] font-['Oswald'] font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center gap-2 hover:bg-[#F0CF6E] transition-colors">
            Discutons de votre projet
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  useSeo("Contact et devis gratuit", "Contactez A.C. ELEC 94 pour vos lots électriques courants forts en Île-de-France. Devis gratuit sous 48h. 06 64 18 92 91.", "/contact");
  const [formData, setFormData] = useState({
    nom: '',
    entreprise: '',
    email: '',
    telephone: '',
    type_projet: '',
    message: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({
        nom: '',
        entreprise: '',
        email: '',
        telephone: '',
        type_projet: '',
        message: '',
        website: ''
      });
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error.response?.status === 429
          ? error.response.data.detail
          : 'Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Header */}
      <section className="bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">Contact</span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
            Demande de Devis
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Décrivez votre projet et nous vous recontacterons rapidement avec une proposition adaptée.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 bg-[#0A0A0A] p-10 text-white">
              <h2 className="font-['Oswald'] text-3xl font-bold mb-8">Informations de Contact</h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="font-['Oswald'] text-lg font-bold text-[#D4A537] mb-4">Téléphone</h3>
                  <div className="space-y-2">
                    <a href="tel:0664189291" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                      <Phone className="w-5 h-5" />
                      06 64 18 92 91
                    </a>
                    <a href="tel:0669258350" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                      <Phone className="w-5 h-5" />
                      06 69 25 83 50
                    </a>
                  </div>
                </div>

                <div>
                  <h3 className="font-['Oswald'] text-lg font-bold text-[#D4A537] mb-4">Email</h3>
                  <a href="mailto:contact@acelec94.com" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                    <Mail className="w-5 h-5" />
                    contact@acelec94.com
                  </a>
                </div>

                <div>
                  <h3 className="font-['Oswald'] text-lg font-bold text-[#D4A537] mb-4">Zone d'Intervention</h3>
                  <p className="flex items-center gap-3 text-white/80">
                    <MapPin className="w-5 h-5" />
                    Île-de-France
                  </p>
                </div>

                <div>
                  <h3 className="font-['Oswald'] text-lg font-bold text-[#D4A537] mb-4">Horaires</h3>
                  <p className="text-white/80">Lundi - Vendredi : 8h - 18h</p>
                  <p className="text-white/80">Samedi : Sur rendez-vous</p>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/10">
                <p className="text-white/60 text-sm mb-4">
                  A.C. ELEC 94<br />
                  Électricité BTP & Tertiaire
                </p>
                
                {/* Social Links */}
                <div className="flex gap-3">
                  <a 
                    href="https://wa.me/33664189291" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-[#D4A537]/60 flex items-center justify-center text-[#D4A537] hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors rounded"
                    title="WhatsApp"
                  >
                    <WhatsAppIcon />
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/claudia-madalina-patrascu-9028a6190" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-[#D4A537]/60 flex items-center justify-center text-[#D4A537] hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors rounded"
                    title="LinkedIn"
                  >
                    <LinkedInIcon />
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61576381062367" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-[#D4A537]/60 flex items-center justify-center text-[#D4A537] hover:bg-[#D4A537] hover:text-[#0A0A0A] transition-colors rounded"
                    title="Facebook"
                  >
                    <FacebookIcon />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3 form-container p-10">
              <h2 className="font-['Oswald'] text-3xl font-bold text-[#F5EFE3] mb-8">Formulaire de Contact</h2>
              
              {submitStatus && (
                <div 
                  data-testid="form-status"
                  className={`p-4 mb-6 ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <input type="text" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] w-px h-px opacity-0" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#F5EFE3] mb-2">Nom *</label>
                    <input
                      type="text"
                      name="nom"
                      data-testid="input-nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border border-[#2E2820] bg-[#141210] focus:border-[#D4A537] transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#F5EFE3] mb-2">Entreprise</label>
                    <input
                      type="text"
                      name="entreprise"
                      data-testid="input-entreprise"
                      value={formData.entreprise}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border border-[#2E2820] bg-[#141210] focus:border-[#D4A537] transition-colors"
                      placeholder="Nom de l'entreprise"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#F5EFE3] mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border border-[#2E2820] bg-[#141210] focus:border-[#D4A537] transition-colors"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#F5EFE3] mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      name="telephone"
                      data-testid="input-telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border border-[#2E2820] bg-[#141210] focus:border-[#D4A537] transition-colors"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#F5EFE3] mb-2">Type de projet</label>
                  <select
                    name="type_projet"
                    data-testid="input-type-projet"
                    value={formData.type_projet}
                    onChange={handleChange}
                    className="w-full h-12 px-4 border border-[#2E2820] bg-[#141210] focus:border-[#D4A537] transition-colors"
                  >
                    <option value="">Sélectionnez le type de projet</option>
                    <option value="tgbt">Raccordement TGBT</option>
                    <option value="chemins-cables">Chemins de câbles</option>
                    <option value="cfo">Tirage CFO</option>
                    <option value="appareillages">Appareillages</option>
                    <option value="complet">Installation complète</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#F5EFE3] mb-2">Message *</label>
                  <textarea
                    name="message"
                    data-testid="input-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-[#2E2820] bg-[#141210] focus:border-[#D4A537] transition-colors resize-none"
                    placeholder="Décrivez votre projet : type de chantier, localisation, délais..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="submit-btn"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      Envoyer ma demande
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p data-testid="contact-privacy-notice" className="text-xs text-[#8C8578]">
                  Vos données sont utilisées uniquement pour répondre à votre demande. <Link to="/politique-de-confidentialite" className="text-[#D4A537] hover:underline">Politique de confidentialité</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/realisations" element={<RealisationsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/realisations/:slug" element={<ChantierDetail />} />
          <Route path="/electricien-btp/:slug" element={<ZonePage />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
        </Routes>
        <Footer />
        <FloatingWhatsApp />
      </BrowserRouter>
    </div>
  );
}

export default App;
